<script setup lang="ts">
import { getInstitutionType } from '@shared/composables/useAppContext';
import { PUSH_STORAGE_KEY, usePush } from '@shared/composables/usePush';
import { useUserStore } from '@shared/stores/user';
import { computed, ref } from 'vue';

import KgrConfirmModal from './KgrConfirmModal.vue';

// 1:1 port of the Angular `kgr-aks-for-push-notification-permissions`
// (customer-apps/notification-preferences). Asks a logged-in user for push permission once,
// unless already granted or a decision was already made (enable/disable persisted).
const userStore = useUserStore();
const push = usePush();

const institutionType = getInstitutionType();

// "Später" hides it for this session; a persisted enable/disable choice (Angular `enabled$`)
// hides it permanently. No stored key === Angular `enabled === undefined` (undecided).
const hidden = ref(false);
const decided = ref(localStorage.getItem(PUSH_STORAGE_KEY) !== null);
const showPermissionNotGrantedHint = ref(false);

const show = computed(
    () => userStore.isLoggedIn && push.permission.value !== 'granted' && !decided.value && !hidden.value,
);

async function onConfirm() {
    const result = await push.requestPermission();
    if (result === 'granted') {
        await push.enable();
        decided.value = true;
    } else {
        showPermissionNotGrantedHint.value = true;
    }
}

async function onDecline() {
    await push.disable();
    decided.value = true;
}

function onCancel() {
    hidden.value = true;
}
</script>

<template>
    <KgrConfirmModal
        :show="show"
        :headline="$t('Push Benachrichtigungen')"
        :cancel-button-label="$t('Später')"
        :show-decline-button="true"
        @confirm="onConfirm"
        @decline="onDecline"
        @cancel="onCancel"
    >
        <template v-if="institutionType === 'club'">
            <div class="kgr-row">
                {{ $t('Dürfen wir dir Push-Nachrichten schicken, damit du keine wichtigen Neuigkeiten aus deinem Verein verpasst?') }}
            </div>
            <div
                v-if="showPermissionNotGrantedHint"
                class="kgr-alert kgr-alert--warning"
            >
                {{ $t('Bitte gib der App die Berechtigung, Push Benachrichtigungen zu versenden.') }}
            </div>
        </template>
        <template v-else>
            <div class="kgr-row">
                {{ $t('Dürfen wir Ihnen Push-Benachrichtigungen senden, damit Sie keine wichtigen Neuigkeiten aus Ihrer Einrichtung verpassen?') }}
            </div>
            <div
                v-if="showPermissionNotGrantedHint"
                class="kgr-alert kgr-alert--warning"
            >
                {{ $t('Bitte gib der App die Berechtigung, Push Benachrichtigungen zu versenden.') }}
            </div>
        </template>
    </KgrConfirmModal>
</template>
